// components/admin/UserManagement.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useAdmin } from "../../context/AdminContext";
import { UserTable } from "./UserTable";
import { UserForm } from "./UserForm";
import { FullAccessModal } from "./FullAccessModal";
import { AdminUserFilters, User } from "../../context/types";
import { revokeFullAccess } from "../../client/api";
import { useNotification } from "../../context/NotificationContext";
import { debounce } from "lodash"; // Import debounce for search optimization

export const UserManagement: React.FC = () => {
  const {
    users,
    loading,
    fetchUsers,
    addUser,
    updateUser,
    giveFullAccess,
    updateWorkspaceLimits,
  } = useAdmin();

  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { addNotification } = useNotification();
  const [searchTerm, setSearchTerm] = useState("");

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers({ search: searchTerm });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleGrantAccess = (user: User) => {
    setSelectedUser(user);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleSubmitAccess = async (
    expiresAt?: Date,
    trialCredits?: number,
    trialCreditsVideoAgent?: number,
    trialCreditsPromptToVideo?: number,
  ) => {
    if (selectedUser) {
      await giveFullAccess(
        selectedUser._id,
        expiresAt,
        trialCredits,
        trialCreditsVideoAgent,
        trialCreditsPromptToVideo,
      );
      setSelectedUser(null);
      fetchUsers(); // Refresh the list
    }
  };

  const handleSubmitUser = async (userData: Partial<User>) => {
    if (editingUser) {
      await updateUser(editingUser._id, userData);
    } else {
      await addUser(userData);
    }
    setShowForm(false);
    setEditingUser(null);
    fetchUsers(); // Refresh the list
  };

  const handleUpdateLimits = async (workspaceId: string, limits: any) => {
    await updateWorkspaceLimits(workspaceId, limits);
    fetchUsers();
  };

  const handleRevokeAccess = async (user: User) => {
    try {
      await revokeFullAccess(user._id);
      addNotification("Full access revoked successfully", "success");
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error("Failed to revoke access:", error);
      addNotification("Failed to revoke access", "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Paper>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users by name, email..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <IconButton onClick={clearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              ),
            }}
          />
        </Paper>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setEditingUser(null);
          setShowForm(true);
        }}
        sx={{ mb: 3 }}
        disabled
      >
        Add New User
      </Button>

      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <UserTable
          users={users}
          onEdit={handleEditUser}
          onGrantAccess={handleGrantAccess}
          onUpdateLimits={handleUpdateLimits}
          onRevokeAccess={handleRevokeAccess}
        />
      )}

      {showForm && (
        <UserForm
          user={editingUser}
          onClose={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
          onSubmit={handleSubmitUser}
        />
      )}

      {selectedUser && (
        <FullAccessModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSubmit={handleSubmitAccess}
        />
      )}
    </Box>
  );
};
